/**
 * Browser history for the app.
 *
 * `createAppHistory` wraps react-router's browser history so that more than one
 * subscriber can observe it: react-router's <HistoryRouter> and the navigation
 * linking below. All reads and writes of the address bar go through it.
 *
 * `createMemoryHistory` is the in-memory record of history entries used by the
 * linking integration, ported from @react-navigation/native 6.1 so that which
 * steps push, replace or go back (and how far) is unchanged. Each entry's id is
 * stored in react-router's location state.
 */
import { createBrowserHistory, History } from '@remix-run/router'
import type { NavigationState } from './routers'
import { createKey } from './routers'

type Update = Parameters<Parameters<History['listen']>[0]>[0]

export interface AppHistory extends History {
  /** Called on every pop (back/forward) before the other listeners. */
  onPop(listener: (update: Update) => void): () => void
}

export function createAppHistory(): AppHistory {
  const inner = createBrowserHistory({ v5Compat: true })
  const popListeners: ((update: Update) => void)[] = []
  const listeners: ((update: Update) => void)[] = []
  let unlistenInner: (() => void) | null = null

  const ensureSubscribed = () => {
    if (unlistenInner) {
      return
    }
    unlistenInner = inner.listen(update => {
      if (update.action === 'POP') {
        popListeners.slice().forEach(listener => listener(update))
      }
      listeners.slice().forEach(listener => listener(update))
    })
  }

  const remove = (list: any[], listener: any) => {
    const index = list.indexOf(listener)
    if (index > -1) {
      list.splice(index, 1)
    }
  }

  return {
    get action() {
      return inner.action
    },
    get location() {
      return inner.location
    },
    createHref: to => inner.createHref(to),
    createURL: to => inner.createURL(to),
    encodeLocation: to => inner.encodeLocation(to),
    push: (to, state) => inner.push(to, state),
    replace: (to, state) => inner.replace(to, state),
    go: delta => inner.go(delta),
    listen(listener) {
      listeners.push(listener)
      ensureSubscribed()
      return () => remove(listeners, listener)
    },
    onPop(listener) {
      popListeners.push(listener)
      ensureSubscribed()
      return () => remove(popListeners, listener)
    }
  }
}

type HistoryRecord = {
  // Unique identifier for this record to match it with the location state
  id: string
  // Navigation state object for the history entry
  state: NavigationState
  // Path of the history entry
  path: string
}

export type MemoryHistory = ReturnType<typeof createMemoryHistory>

export function createMemoryHistory(browserHistory: AppHistory) {
  let index = 0
  let items: HistoryRecord[] = []

  const currentId = (): string | undefined =>
    (browserHistory.location.state as any)?.id

  // Pending callbacks for `history.go(n)`
  // We might modify the callback stored if it was interrupted, so we have a ref to identify it
  const pending: { ref: unknown; cb: (interrupted?: boolean) => void }[] = []
  // `history.go(n)` handlers waiting for their pop, and external pop listeners
  const goHandlers: (() => void)[] = []
  const externalListeners: (() => void)[] = []

  browserHistory.onPop(() => {
    // External changes (the user pressing back/forward) only, not `history.go(n)`
    if (!pending.length) {
      externalListeners.slice().forEach(listener => listener())
    }
    goHandlers.splice(0).forEach(handler => handler())
  })

  const interrupt = () => {
    // If another history operation was performed we need to interrupt existing ones
    // This makes sure that calls such as `history.replace` after `history.go` don't happen
    // Since otherwise it won't be correct if something else has changed
    pending.forEach(it => {
      const finish = it.cb
      it.cb = () => finish(true)
    })
  }

  const history = {
    get index(): number {
      // We store an id in the state instead of an index
      // Index could get out of sync with in-memory values if page reloads
      const id = currentId()

      if (id) {
        const index = items.findIndex(item => item.id === id)

        return index > -1 ? index : 0
      }

      return 0
    },

    get(index: number) {
      return items[index]
    },

    backIndex({ path }: { path: string }) {
      // We need to find the index from the element before current to get closest path to go back to
      for (let i = index - 1; i >= 0; i--) {
        const item = items[i]

        if (item.path === path) {
          return i
        }
      }

      return -1
    },

    push({ path, state }: { path: string; state: NavigationState }) {
      interrupt()

      const id = createKey()

      // When a new entry is pushed, all the existing entries after index will be inaccessible
      // So we remove any existing entries after the current index to clean them up
      items = items.slice(0, index + 1)

      items.push({ path, state, id })
      index = items.length - 1

      browserHistory.push(path, { id })
    },

    replace({ path, state }: { path: string; state: NavigationState }) {
      interrupt()

      const id = currentId() ?? createKey()

      // Need to keep the hash part of the path if there was no previous history entry
      // or the previous history entry had the same path
      let pathWithHash = path

      if (!items.length || items.findIndex(item => item.id === id) < 0) {
        // There are two scenarios for creating an array with only one history record:
        // - When loaded id not found in the items array, this function by default will replace
        //   the first item. We need to keep only the new updated object, otherwise it will break
        //   the page when navigating forward in history.
        // - This is the first time any state modifications are done
        //   So we need to push the entry as there's nothing to replace
        pathWithHash = pathWithHash + window.location.hash
        items = [{ path: pathWithHash, state, id }]
        index = 0
      } else {
        if (items[index].path === path) {
          pathWithHash = pathWithHash + window.location.hash
        }
        items[index] = { path, state, id }
      }

      browserHistory.replace(pathWithHash, { id })
    },

    // `history.go(n)` is asynchronous, there are couple of things to keep in mind:
    // - it won't do anything if we can't go `n` steps, the `popstate` event won't fire.
    // - each `history.go(n)` call will trigger a separate `popstate` event with correct location.
    // - the `popstate` event fires before the next frame after calling `history.go(n)`.
    // This method differs from `history.go(n)` in the sense that it'll go back as many steps it can.
    go(n: number) {
      interrupt()

      // To guard against unexpected navigation out of the app we will assume that browser history is only as deep as the length of our memory
      // history. If we don't have an item to navigate to then update our index and navigate as far as we can without taking the user out of the app.
      const nextIndex = index + n
      const lastItemIndex = items.length - 1
      if (n < 0 && !items[nextIndex]) {
        // Attempted to navigate beyond the first index. Negating the current index will align the browser history with the first item.
        n = -index
        index = 0
      } else if (n > 0 && nextIndex > lastItemIndex) {
        // Attempted to navigate past the last index. Calculate how many indices away from the last index and go there.
        n = lastItemIndex - index
        index = lastItemIndex
      } else {
        index = nextIndex
      }

      if (n === 0) {
        return undefined
      }

      // When we call `history.go`, a pop will fire when there's history to go back to
      // So we need to somehow handle following cases:
      // - There's history to go back, `history.go` is called, and the pop fires
      // - `history.go` is called multiple times, we need to resolve on respective pops
      // - No history to go back, but `history.go` was called, browser has no API to detect it
      return new Promise<void>((resolve, reject) => {
        const done = (interrupted?: boolean) => {
          clearTimeout(timer)

          if (interrupted) {
            reject(new Error('History was changed during navigation.'))
            return
          }

          // There seems to be a bug in Chrome regarding updating the title
          // If we set a title just before calling `history.go`, the title gets lost
          // However the value of `document.title` is still what we set it to
          // It's just not displayed in the tab bar
          // To update the tab bar, we need to reset the title to something else first (e.g. '')
          // And set the title to what it was before so it gets applied
          // It won't work without setting it to empty string coz otherwise title isn't changing
          // Which means that the browser won't do anything after setting the title
          const { title } = window.document

          window.document.title = ''
          window.document.title = title

          resolve()
        }

        pending.push({ ref: done, cb: done })

        // If navigation didn't happen within 100ms, assume that it won't happen
        // This may not be accurate, but hopefully it won't take so much time
        // In Chrome, navigation seems to happen instantly in next microtask
        // But on Firefox, it seems to take much longer, around 50ms from our testing
        // We're using a hacky timeout since there doesn't seem to be way to know for sure
        const timer = setTimeout(() => {
          const index = pending.findIndex(it => it.ref === done)

          if (index > -1) {
            pending[index].cb()
            pending.splice(index, 1)
          }
        }, 100)

        const onPop = () => {
          const id = currentId()
          const currentIndex = items.findIndex(item => item.id === id)

          // Fix createMemoryHistory.index variable's value
          // as it may go out of sync when navigating in the browser.
          index = Math.max(currentIndex, 0)

          const last = pending.pop()

          last?.cb()
        }

        goHandlers.push(onPop)
        browserHistory.go(n)
      })
    },

    // Pops are triggered when history changes, except for push and replace.
    // If we call `history.go(n)` ourselves, we don't want it to trigger the listener.
    // Here we normalize it so that only external changes (e.g. user pressing back/forward) trigger the listener.
    listen(listener: () => void) {
      externalListeners.push(listener)

      return () => {
        const i = externalListeners.indexOf(listener)
        if (i > -1) {
          externalListeners.splice(i, 1)
        }
      }
    }
  }

  return history
}
